// src/App.tsx
import React, { useLayoutEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import LoginPage from "pages/auth/LoginPage";
import ForgotPasswordPage from "pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "pages/auth/ResetPasswordPage";

import ProtectedRoute from "./routes/ProtectedRoute";
import AdminRoute from "./routes/AdminRoute";
import SuperAdminRoute from "./routes/SuperAdminRoute";

import AdminHomePage from "pages/admin/AdminHomePage";
import AdminPackagesPage from "pages/admin/AdminPackagesPage";
import AdminClinicsPage from "pages/admin/AdminClinicsPage";
import AdminNotificationsPage from "pages/admin/AdminNotificationsPage";
import AdminOrdersListPage from "pages/admin/orders/AdminOrdersListPage";
import AdminOrderEditPage from "pages/admin/orders/AdminOrderEditPage";
import AdminAbandonedCheckoutsPage from "pages/admin/AdminAbandonedCheckoutsPage";
import AdminFunnelCampaignsPage from "pages/admin/AdminFunnelCampaignsPage";
import NoAdminAccessPage from "pages/admin/NoAdminAccessPage";
import SuperAdminUsersPage from "pages/superadmin/SuperAdminUsersPage";

import FrameLayout from "./layouts/FrameLayout";
import HoldingPage from "pages/holding/HoldingPage";

import DevAuthBadge from "components/DevAuthBadge";

/**
 * Every admin path this app serves. The customer pages live in the new
 * frontend; until launch `/` is the public holding page, and any other path
 * redirects there.
 */
export const ADMIN_ROUTE_PATHS = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/no-access",
  "/admin",
  "/admin/packages",
  "/admin/clinics",
  "/admin/notifications",
  "/admin/orders",
  "/admin/orders/:id",
  "/admin/abandoned-checkouts",
  "/admin/funnel-campaigns",
  "/superadmin/admins",
] as const;

/**
 * The admin routes. Scrolls to the top on each new page.
 */
const AdminRoutes: React.FC = () => {
  const location = useLocation();

  // Prevent double-triggering scroll work within the same pathname render.
  const lastAutoScrolledPathRef = useRef<string | null>(null);

  /**
   * Scroll to top when the new route renders. In-page scroll triggers
   * (typically a child useEffect) can override after mount if needed.
   *
   * Opt-out support (future-proofing):
   * If you navigate with location state like:
   *   navigate("/path", { state: { disableScrollToTopOnNavigate: true } })
   * then the auto scroll is skipped for that navigation.
   */
  useLayoutEffect(() => {
    const path = location.pathname;

    if (lastAutoScrolledPathRef.current === path) return;
    lastAutoScrolledPathRef.current = path;

    const state = location.state as unknown as {
      disableScrollToTopOnNavigate?: boolean;
    } | null;

    if (state?.disableScrollToTopOnNavigate) return;

    let reducedMotion = false;
    try {
      reducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      reducedMotion = false;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: reducedMotion ? "auto" : "smooth",
    });
  }, [location.pathname, location.state]);

  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/no-access" element={<NoAdminAccessPage />} />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminHomePage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/packages"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminPackagesPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clinics"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminClinicsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminNotificationsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminOrdersListPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders/:id"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminOrderEditPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/abandoned-checkouts"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminAbandonedCheckoutsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/funnel-campaigns"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminFunnelCampaignsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/superadmin/admins"
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <SuperAdminUsersPage />
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* The holding page has its own full-bleed design, so it sits outside the admin frame. */}
        <Route path="/" element={<HoldingPage />} />
        <Route
          path="*"
          element={
            <FrameLayout>
              <AdminRoutes />
            </FrameLayout>
          }
        />
      </Routes>
      <DevAuthBadge />
    </Router>
  );
};

export default App;
